// src/useTranslations.js
import { useState, useEffect, useCallback } from "react";
import translations from "./translations";
import storage from "./services/StorageAdapter";

const LANGUAGE_EVENT = 'rms_language_changed';
const STORAGE_KEY = 'rms_settings';

// Read language from SettingsService storage
function getStoredLanguage() {
  const settings = storage.getJSON(STORAGE_KEY, {});
  return settings.ui?.language || 'en';
}

// Module-level current language (avoids reading localStorage on every t() call)
let currentLanguage = getStoredLanguage();

/**
 * Translation function. Resolves dot-notation keys against the current language.
 * Supports template variables: t('key', { count: 5 }) replaces {{count}} in the string.
 * @param {string} keyPath - e.g. "sidebar.dashboard"
 * @param {Object} [params] - template variables to interpolate
 * @returns {string} translated string or the key itself if not found
 */
export const t = (keyPath, params) => {
  const keys = keyPath.split(".");
  let result = translations[currentLanguage] || translations.en;

  for (const key of keys) {
    if (!result || !result[key]) {
      // Fallback to English
      let fallback = translations.en;
      for (const k of keys) {
        if (!fallback || !fallback[k]) return keyPath;
        fallback = fallback[k];
      }
      result = fallback;
      break;
    }
    result = result[key];
  }

  if (typeof result === 'string' && params) {
    return result.replace(/\{\{(\w+)\}\}/g, (_, key) =>
      params[key] !== undefined ? params[key] : `{{${key}}}`
    );
  }

  return result;
};

/**
 * Set the active language. Updates localStorage and dispatches event for reactivity.
 * @param {'en'|'sv'} lang
 */
export const setLanguage = (lang) => {
  if (lang !== 'en' && lang !== 'sv') return;
  currentLanguage = lang;

  // Persist till SettingsService storage (legacy, bakåtkompat).
  const settings = storage.getJSON(STORAGE_KEY, {});
  if (!settings.ui) settings.ui = {};
  settings.ui.language = lang;
  storage.setJSON(STORAGE_KEY, settings);

  // INFRA-172: också persist till backend-user-preferences så språket följer
  // med användaren vid inloggning från annan enhet. Sync är fire-and-forget.
  try {
    // Dynamisk import för att undvika cirkulärt beroende mellan usePreferences
    // (som läser sessionService) och translations (som importeras överallt).
    const { setPreference } = require('./hooks/usePreferences');
    setPreference('app.language', lang).catch(() => {});
  } catch (_) { /* hook kanske inte är laddad ännu under tidig bootstrap */ }

  // Notify all subscribers
  window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT, { detail: { language: lang } }));
};

/**
 * Get current language code.
 * @returns {'en'|'sv'}
 */
export const getCurrentLanguage = () => currentLanguage;

/**
 * Temporarily override the active language for the duration of a synchronous function call.
 * Used by export utilities to generate reports in a specific language without affecting UI.
 * @param {'en'|'sv'|null} lang - language to use, or null to use current
 * @param {Function} fn - synchronous function to call with the override active
 * @returns {*} return value of fn
 */
export const withExportLanguage = (lang, fn) => {
  if (!lang || (lang !== 'en' && lang !== 'sv')) {
    return fn();
  }
  const prev = currentLanguage;
  currentLanguage = lang;
  try {
    return fn();
  } finally {
    currentLanguage = prev;
  }
};

/**
 * Async init function to re-read language after storage has been hydrated.
 * Call from App.js useEffect after storage.hydrate() so the module-level
 * currentLanguage variable reflects the persisted value even on async backends.
 */
export const initTranslations = async () => {
  await storage.hydrate([STORAGE_KEY]);
  const settings = storage.getJSON(STORAGE_KEY, {});
  let lang = settings.ui?.language || 'en';

  // INFRA-172: om backend-preferences har språk satt — det vinner (följer
  // användaren mellan enheter). Kräver att ensurePreferencesSync har körts
  // INNAN initTranslations (App.js wirar det så).
  try {
    const { getPreference } = require('./hooks/usePreferences');
    const remoteLang = getPreference('app.language', null);
    if (remoteLang === 'sv' || remoteLang === 'en') lang = remoteLang;
  } catch (_) { /* ok */ }

  if (lang !== currentLanguage && (lang === 'en' || lang === 'sv')) {
    currentLanguage = lang;
    window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT, { detail: { language: lang } }));
  }
};

/**
 * React hook that subscribes to language changes and triggers re-render.
 * Call this in any component that uses t() and needs to react to language switches.
 * @returns {'en'|'sv'} current language
 */
export const useLanguage = () => {
  const [lang, setLang] = useState(currentLanguage);

  const handleChange = useCallback((e) => {
    currentLanguage = e.detail.language;
    setLang(e.detail.language);
  }, []);

  useEffect(() => {
    // Sync in case language changed between render cycles
    if (lang !== currentLanguage) {
      setLang(currentLanguage);
    }
    window.addEventListener(LANGUAGE_EVENT, handleChange);
    return () => window.removeEventListener(LANGUAGE_EVENT, handleChange);
  }, [handleChange, lang]);

  return lang;
};
